import React from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Heading from "@components/layout/Heading";
import Alert from "@components/shared/Alert";
import Tag from "@components/shared/Tag";
import TrashIcon from "@components/icons/TrashIcon";
import DropzoneBox from "@components/shared/DropzoneBox";
import NoteComposer from "@components/notes/NoteComposer";
import { readXlsxFile } from "@utils/subscriberSyncUpload/readXlsxFile";
import {
  parseUsersSheet,
  ParseError,
} from "@utils/subscriberSyncUpload/parseUsersSheet";
import { CrmRow } from "@utils/subscriberSyncUpload/compareRow";
import { useSubscriberSyncUpload } from "@context/subscriberSyncUpload/Provider";
import { useCreateSubscriberSyncDraft } from "@hooks/mutation/useCreateSubscriberSyncDraft";
import { toastError } from "@utils/toast";

const errorKey = (error: ParseError): string => {
  switch (error.code) {
    case "invalid_header":
      return "subscriber_sync_upload.validation.invalid_header";
    case "missing_header_row":
      return "subscriber_sync_upload.validation.missing_header";
    case "empty_file":
      return "subscriber_sync_upload.validation.empty_file";
  }
};

const UploadFile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: level1ResourceId = "" } = useParams();
  const { state, dispatch } = useSubscriberSyncUpload();
  const [file, setFile] = React.useState<File | null>(null);
  const [rows, setRows] = React.useState<CrmRow[] | null>(null);
  const [errors, setErrors] = React.useState<string[]>([]);
  const createDraft = useCreateSubscriberSyncDraft();

  const reset = () => {
    setFile(null);
    setRows(null);
    setErrors([]);
  };

  const handleFilePicked = async (picked: File) => {
    setFile(picked);
    setRows(null);
    setErrors([]);

    try {
      const sheet = await readXlsxFile(picked);
      const result = parseUsersSheet(sheet);
      if (!result.ok) {
        setErrors([t(errorKey(result.error))]);
        return;
      }
      if (result.rows.length === 0) {
        setErrors([t("subscriber_sync_upload.validation.empty_file")]);
        return;
      }
      setRows(result.rows);
    } catch (err: any) {
      setErrors([
        t("subscriber_sync_upload.message.error", {
          error: err?.message ?? "unknown",
        }),
      ]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files: File[]) => {
      if (files[0]) handleFilePicked(files[0]);
    },
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
  });

  const isValidFile = Boolean(rows && errors.length === 0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!rows || !state.level1ResourceId) return;
    createDraft.mutate(
      { level1ResourceId: state.level1ResourceId, rows, fileName: file?.name },
      {
        onSuccess: ({ uploadId, rows: serverRows }) =>
          dispatch({ type: "trial-run-loaded", uploadId, rows: serverRows }),
        onError: (err: any) =>
          toastError(
            t("subscriber_sync_upload.message.error", {
              error: err?.response?.data?.message ?? err?.message ?? "unknown",
            }),
          ),
      },
    );
  };

  const handleCancel = () => {
    dispatch({ type: "reset" });
    navigate(
      level1ResourceId
        ? `/polestar/level1wrs/${level1ResourceId}#2`
        : "/polestar/subscribers/sync_uploads",
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col grow gap-6 p-6">
      <Heading light>{t("subscriber_sync_upload.select_file")}</Heading>

      {errors.length ? (
        <Alert type="error">
          <h4 className="text-lg mb-3">
            {t("subscriber_sync_upload.validation.invalid")}
          </h4>
          <ul className="space-y-1 pl-4 list-disc">
            {errors.map(message => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="max-w-lg">
        <DropzoneBox
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          subtitle={t("subscriber_sync_upload.supported_file")}
          multiple={false}
        />

        {file ? (
          <table className="w-full text-sm border-collapse border border-gray-100 rounded mt-6">
            <thead>
              <tr>
                <th className="bg-gray-50 p-2 text-left font-normal">
                  {t("subscriber_sync_upload.file_to_upload")}
                </th>
                <th
                  className="bg-gray-50 p-2 text-left font-normal"
                  colSpan={2}
                >
                  {t("subscriber_sync_upload.format_check")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border-t border-gray-100">{file.name}</td>
                <td className="p-2 border-t border-gray-100">
                  <Tag status={isValidFile ? "success" : "error"}>
                    {isValidFile ? t("common.valid") : t("common.invalid")}
                  </Tag>
                  {isValidFile && rows ? (
                    <span className="ml-2 text-gray-600">
                      {t("subscriber_sync_upload.parsed_rows", {
                        count: rows.length,
                      })}
                    </span>
                  ) : null}
                </td>
                <td className="p-2 border-t border-gray-100 text-right">
                  <button
                    type="button"
                    className="p-2 btn-outline-primary inline-flex items-center gap-1 text-xs rounded-sm px-3 py-1"
                    onClick={reset}
                  >
                    <TrashIcon className="w-3 h-3" />
                    {t("common.delete")}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </div>

      <hr className="border-gray-200" />

      {/* FR-6 session note on the synchronisation record, entered here (mirrors bulk meter upload).
          Held in wizard state so it survives step navigation; committed at finalize. */}
      <NoteComposer
        value={state.sessionNote}
        onChange={note => dispatch({ type: "set-session-note", note })}
        idPrefix="subscriber-sync-note"
      />

      <div className="grow" />

      <footer className="flex gap-4 border-t border-gray-300 p-6 pb-0 -mx-6">
        {!level1ResourceId ? (
          <button
            type="button"
            className="btn-outline-primary"
            onClick={() => dispatch({ type: "back-to-select-scheme" })}
          >
            {t("common.prev_step")}
          </button>
        ) : null}
        <button
          type="submit"
          className="btn-primary"
          disabled={!isValidFile || createDraft.isLoading}
        >
          {t("common.next_step")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={handleCancel}
        >
          {t("common.cancel")}
        </button>
      </footer>
    </form>
  );
};

export default UploadFile;
